import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button, Input } from '@pijma/crypto'
import { useBaseMask } from '@pijma/input'

import { Dispatch, RootState } from '@stores'
import {
  charIsCardHolderSymbol,
  isCardHolderRegexp,
  replaceNotCardHolderSymbol,
} from '@utils'
import cardValidator from 'card-validator'
import { CardNumberVerification } from 'card-validator/dist/card-number'
import styled from 'styled-components'

import { usePatternMask } from '../../../../../hooks/usePattern'
import {
  SESSION_RECEIVER_BANK_CARD_KEY,
  SESSION_RECEIVER_KEY,
} from '../../../../../stores/payform/payForm'
import { TPaymentGateway } from '../../../../../stores/types/TPaymentGatewayState'
import InputValidation from '../../../../../validation/InputValidation'
import { H4 } from '../../../../common/header/Headings'
import { Island } from '../../../../common/island'
import NavigationBar from '../../../../common/navigation/NavigationBar'
import { BodyText500, CaptionText500 } from '../../../../common/text/BodyText'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 20px;
`

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px 8px;
`
const CountryList = styled.div`
  display: flex;
  width: 100%;
  padding: 0 24px 24px;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 24px 20px 20px;
`

const SourceTitle = styled.div`
  display: flex;
  flex-direction: column;
  white-space: pre-wrap;
  width: 100%;
  padding: 20px 24px 16px;
`

const NavigationBarWrapper = styled.div`
  padding: 0 4px;
  display: flex;
  width: 100%;
  align-items: start;
`

const BackContainer = styled.div`
  margin: 24px 0 0 0;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

interface StateProps {
  bankCard?: string
  receiver?: string
  paymentGateway?: TPaymentGateway
  paymentBlocking?: boolean
}

interface DispatchProps {
  setBankCard: (bankCard: string) => void
  setReceiver: (receiver: string) => void
  doPaymentBlocking: () => void
  resetPaymentBlocking: () => void
}

type Props = StateProps & DispatchProps

const SellSourceCardPage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()
  // validation
  //// state
  const [bankCardValidation, setBankCardValidation] = useState<InputValidation>(
    {
      valid: true,
    },
  )
  const [receiverValidation, setReceiverValidation] = useState<InputValidation>(
    {
      valid: true,
    },
  )
  const [formValid, setFormValid] = useState<boolean>(false)

  //// functions
  const validateBankCard = useCallback(
    (onlyValid = false) => {
      let cardNumberVerification: CardNumberVerification | undefined
      if (props.bankCard) {
        cardNumberVerification = cardValidator.number(props.bankCard)
        if (cardNumberVerification.isValid) {
          setBankCardValidation({ valid: true })
          return
        }
      }
      if (onlyValid) return
      if (cardNumberVerification?.card?.type != 'visa') {
        setBankCardValidation({
          valid: false,
          message: intl.formatMessage({
            id: 'sellSourceCardPage.bankCard.error.visa',
          }),
        })
        return
      }
      if (!cardNumberVerification?.isValid) {
        setBankCardValidation({
          valid: false,
          message: intl.formatMessage({
            id: 'sellSourceCardPage.bankCard.error.wrong',
          }),
        })
        return
      }
      setBankCardValidation({
        valid: false,
        message: intl.formatMessage({
          id: 'sellSourceCardPage.bankCard.error.empty',
        }),
      })
    },
    [props.bankCard],
  )
  const validateReceiver = useCallback(
    (onlyValid = false) => {
      if (props.receiver && isCardHolderRegexp.test(props.receiver)) {
        setReceiverValidation({ valid: true })
        return true
      }
      if (onlyValid) return false
      if (props.receiver == undefined) {
        setReceiverValidation({
          valid: false,
          message: intl.formatMessage({
            id: 'sellSourceCardPage.nameAndSurnameValidation',
          }),
        })
        return false
      }
      setReceiverValidation({
        valid: false,
        message: intl.formatMessage({
          id: 'sellSourceCardPage.invalidNameAndSurnameValidation',
        }),
      })
      return false
    },
    [props.receiver],
  )
  //// effects
  useEffect(() => {
    if (!bankCardValidation.valid) {
      setFormValid(false)
    }
  }, [bankCardValidation])

  useEffect(() => {
    if (!receiverValidation.valid) {
      setFormValid(false)
    }
  }, [receiverValidation])

  useEffect(() => {
    setFormValid(
      props.receiver !== undefined &&
        props.bankCard !== undefined &&
        bankCardValidation.valid &&
        receiverValidation.valid,
    )
  }, [bankCardValidation, receiverValidation, props.receiver, props.bankCard])

  // Pijma input masks
  const receiverMask = useBaseMask({
    type: 'text',
    format: replaceNotCardHolderSymbol,
    isValidInputCharacter: charIsCardHolderSymbol,
    removeFormatting: replaceNotCardHolderSymbol,
    value: props.receiver,
    onBlur: () => {
      validateReceiver()
    },
    onValueChange: ({ formattedValue }) => {
      props.setReceiver(formattedValue)
      validateReceiver(true)
    },
  })
  const bankCardMask = usePatternMask({
    format: 'dddd dddd dddd dddd',
    value: props.bankCard,
    onBlur: () => {
      validateBankCard()
    },
    onValueChange: ({ formattedValue }) => {
      props.setBankCard(formattedValue)
      validateBankCard(true)
    },
  })

  const goToSumPage = () => {
    validateBankCard()
    validateReceiver()
    sessionStorage.setItem(SESSION_RECEIVER_KEY, props.receiver + '')
    sessionStorage.setItem(SESSION_RECEIVER_BANK_CARD_KEY, props.bankCard + '')
    redirectTo(`../../sum`)
  }

  return (
    <Container>
      <NavigationBarWrapper>
        <NavigationBar
          paymentMethodRedirectLink={'../../source-select'}
          paymentDisabled
          sumDisabled
          noPaymentMethod={true}
        />
      </NavigationBarWrapper>
      <Island size="M" bgColor="primary">
        <SourceTitle data-testid="userdata.header">
          <H4>{intl.formatMessage({ id: 'sellSourceCardPage.title' })}</H4>
        </SourceTitle>
        <InputContainer>
          <Input
            {...bankCardMask}
            label={intl.formatMessage({ id: 'sellSourceCardPage.cardLabel' })}
            width={'100%'}
            disabled={props.paymentBlocking}
            type={'text'}
            inputMode={'decimal'}
            placeholder={'0000 0000 0000 0000'}
            errorMessage={bankCardValidation.message}
            invalid={!bankCardValidation.valid}
            allowClear={true}
          />
        </InputContainer>
        <CountryList>
          <a
            href="/docs/visaCountries.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#337ab7', textDecoration: 'none' }}
          >
            <CaptionText500 color={'#217aac'}>
              {intl.formatMessage({
                id: 'sellSourceCardPage.visaAvailableCountries',
              })}
            </CaptionText500>
          </a>
        </CountryList>
        <InputContainer>
          <Input
            {...receiverMask}
            width={'100%'}
            disabled={props.paymentBlocking}
            type={'text'}
            description={intl.formatMessage({
              id: 'sellSourceCardPage.receiverDescription',
            })}
            placeholder={intl.formatMessage({
              id: 'sellSourceCardPage.receiverPlaceholder',
            })}
            errorMessage={receiverValidation.message}
            invalid={!receiverValidation.valid}
            allowClear={true}
          />
        </InputContainer>
        <ButtonContainer>
          <Button
            width={'100%'}
            size={'m'}
            disabled={!formValid}
            onPress={() => goToSumPage()}
          >
            {intl.formatMessage({ id: 'sellSourceCardPage.goToSumPage' })}
          </Button>
        </ButtonContainer>
      </Island>
      <BackContainer
        onClick={() => redirectTo('../../provider-select')}
        data-testid="payment-methods.back"
      >
        <BodyText500>
          {intl.formatMessage({ id: 'sellSourceCardPage.back' })}
        </BodyText500>
      </BackContainer>
    </Container>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  bankCard: state.payForm?.receiverBankCard,
  receiver: state.payForm?.receiver,
  paymentGateway: state.payForm?.paymentGateway,
  paymentBlocking: state.payForm?.paymentBlocking,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setBankCard: (bankCard: string) =>
      dispatch.payForm.set({
        receiverBankCard: bankCard,
      }),
    setReceiver: (receiver: string) =>
      dispatch.payForm.set({
        receiver,
      }),
    doPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: true,
      }),
    resetPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: false,
      }),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SellSourceCardPage)
